import { useEffect, useState } from "react";

type IdleWindow = Window & {
  requestIdleCallback?: (
    callback: () => void,
    options?: { timeout: number },
  ) => number;
  cancelIdleCallback?: (handle: number) => void;
};

/**
 * Defers non-essential public enhancements until the initial document has had
 * time to paint. A deliberate interaction always wins, while browser-generated
 * focus and scroll restoration must not start public RPC chains during LCP.
 */
export function useDeferredPublicEnhancement(delayMs = 10_000) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;

    const idleWindow = window as IdleWindow;
    let delayHandle: number | undefined;
    let idleHandle: number | undefined;
    let cancelled = false;

    const activate = () => {
      if (cancelled) return;
      setReady(true);
    };
    const schedule = () => {
      delayHandle = window.setTimeout(
        () => {
          if (idleWindow.requestIdleCallback) {
            idleHandle = idleWindow.requestIdleCallback(activate, {
              timeout: 1_000,
            });
          } else {
            activate();
          }
        },
        Math.max(0, delayMs),
      );
    };

    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });

    const interactionEvents = ["pointerdown", "touchstart"] as const;
    for (const eventName of interactionEvents) {
      window.addEventListener(eventName, activate, {
        once: true,
        passive: true,
      });
    }
    window.addEventListener("keydown", activate, { once: true });

    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
      for (const eventName of interactionEvents) {
        window.removeEventListener(eventName, activate);
      }
      window.removeEventListener("keydown", activate);
      if (delayHandle !== undefined) window.clearTimeout(delayHandle);
      if (idleHandle !== undefined) idleWindow.cancelIdleCallback?.(idleHandle);
    };
  }, [delayMs, ready]);

  return ready;
}
